import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowPathIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  EyeIcon,
  EyeSlashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowsPointingInIcon,
  MagnifyingGlassIcon,
  InformationCircleIcon,
  PhotoIcon,
  QueueListIcon,
} from "@heroicons/react/24/outline";
import { MapPinIcon as PinSolid } from "@heroicons/react/24/solid";
import { MapPinIcon } from "@heroicons/react/24/outline";
import { listAdminCategories, updateAdminCategory, setPinnedCategories, mergeCategory, listAdminRubrics } from "../api/categoriesAdmin";
import SubcategoriesDialog from "../components/categories/SubcategoriesDialog";
import RubricsPanel from "../components/categories/RubricsPanel";

const fmt = (n) => Number(n || 0).toLocaleString("ru-RU");
const inputCls =
  "w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary " +
  "placeholder:text-text-muted focus:border-[#4F45E5] focus:outline-none focus:ring-2 focus:ring-[#4F45E5]/30";

const Stat = ({ label, value, strong }) => (
  <div className="w-16 text-right">
    <div className={`text-sm tabular-nums ${strong ? "font-semibold text-text-primary" : "text-text-secondary"}`}>{fmt(value)}</div>
    <div className="text-[10px] text-text-muted">{label}</div>
  </div>
);

const Row = ({ c, maxScore, byId, rubricName, onPin, onMove, onToggleHidden, onEdit, onMerge, onSubs, busy, first, last }) => (
  <li className={`group flex flex-wrap items-center gap-3 px-4 py-2.5 hover:bg-bg-tertiary/40 ${busy ? "opacity-50" : ""} ${c.is_hidden ? "opacity-60" : ""}`}>
    <span className={`w-7 text-center text-sm font-semibold tabular-nums ${c.is_pinned ? "text-[#7C3AED]" : "text-text-muted"}`}>{c.position ?? "—"}</span>
    {c.image_url ? (
      <img src={c.image_url} alt="" loading="lazy" className="h-10 w-10 shrink-0 rounded-lg bg-bg-tertiary object-cover" />
    ) : (
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-bg-tertiary text-text-muted">
        <PhotoIcon className="h-5 w-5" />
      </span>
    )}
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="truncate text-sm font-medium text-text-primary">{c.name}</span>
        <span className="font-mono text-[11px] text-text-muted">#{c.id}</span>
        {c.is_pinned && <span className="rounded border border-[#4F45E5]/40 bg-[#4F45E5]/10 px-1.5 text-[10px] font-semibold text-[#7C3AED] dark:text-indigo-300">закреплена</span>}
        {c.is_hidden && <span className="rounded border border-border-primary px-1.5 text-[10px] text-text-muted">скрыта</span>}
        {c.rubric_id ? (
          <span className="rounded border border-border-primary bg-bg-primary px-1.5 text-[10px] text-text-secondary" title="Рубрика на главной сайта">
            {rubricName[c.rubric_id] || `рубрика #${c.rubric_id}`}
          </span>
        ) : (
          !c.is_hidden && (
            <span className="rounded border border-rose-500/40 bg-rose-500/10 px-1.5 text-[10px] font-semibold text-rose-500" title="Не видна в группах на главной сайта">
              без рубрики
            </span>
          )
        )}
        {c.duplicates.length > 0 && !c.is_hidden && (
          <span className="rounded border border-amber-500/40 bg-amber-500/10 px-1.5 text-[10px] font-semibold text-amber-500" title={c.duplicates.map((d) => `#${d}`).join(", ")}>
            дубль {c.duplicates.filter((d) => !byId[d]?.is_hidden).map((d) => `#${d}`).join(", ") || "(скрыт)"}
          </span>
        )}
      </div>
      <div className="mt-1 flex items-center gap-2">
        <div className="h-1 w-24 overflow-hidden rounded-full bg-bg-tertiary">
          <div className="h-full rounded-full bg-accent-grad" style={{ width: `${maxScore ? (c.score / maxScore) * 100 : 0}%` }} />
        </div>
        <span className="truncate text-[11px] text-text-muted">
          {c.kz_name || "—"} ·{" "}
          <button type="button" onClick={() => onSubs(c)} className="underline decoration-dotted underline-offset-2 hover:text-text-primary">
            {c.subcategories_count} подкат.
          </button>
        </span>
      </div>
    </div>
    <div className="flex items-center gap-1">
      <Stat label="поиск/30д" value={c.stats.searches_30d} strong />
      <Stat label="активных" value={c.stats.active_announcements} />
      <Stat label="просм." value={c.stats.views} />
    </div>
    <div className="flex items-center gap-0.5">
      {c.is_pinned && (
        <>
          <button type="button" disabled={first} onClick={() => onMove(c, -1)} title="Выше" aria-label="Выше" className="rounded-lg p-1.5 text-text-muted hover:bg-bg-tertiary hover:text-text-primary disabled:opacity-30">
            <ChevronUpIcon className="h-4 w-4" />
          </button>
          <button type="button" disabled={last} onClick={() => onMove(c, 1)} title="Ниже" aria-label="Ниже" className="rounded-lg p-1.5 text-text-muted hover:bg-bg-tertiary hover:text-text-primary disabled:opacity-30">
            <ChevronDownIcon className="h-4 w-4" />
          </button>
        </>
      )}
      {!c.is_hidden && (
        <button type="button" onClick={() => onPin(c)} title={c.is_pinned ? "Открепить" : "Закрепить наверху"} aria-label="Закрепить" className={`rounded-lg p-1.5 hover:bg-bg-tertiary ${c.is_pinned ? "text-[#7C3AED]" : "text-text-muted hover:text-text-primary"}`}>
          {c.is_pinned ? <PinSolid className="h-4 w-4" /> : <MapPinIcon className="h-4 w-4" />}
        </button>
      )}
      <button type="button" onClick={() => onToggleHidden(c)} title={c.is_hidden ? "Показать" : "Скрыть"} aria-label={c.is_hidden ? "Показать" : "Скрыть"} className="rounded-lg p-1.5 text-text-muted hover:bg-bg-tertiary hover:text-text-primary">
        {c.is_hidden ? <EyeIcon className="h-4 w-4" /> : <EyeSlashIcon className="h-4 w-4" />}
      </button>
      <button type="button" onClick={() => onSubs(c)} title="Подкатегории" aria-label="Подкатегории" className="rounded-lg p-1.5 text-text-muted hover:bg-bg-tertiary hover:text-text-primary">
        <QueueListIcon className="h-4 w-4" />
      </button>
      <button type="button" onClick={() => onEdit(c)} title="Редактировать" aria-label="Редактировать" className="rounded-lg p-1.5 text-text-muted hover:bg-bg-tertiary hover:text-text-primary">
        <PencilSquareIcon className="h-4 w-4" />
      </button>
      {!c.is_hidden && (
        <button type="button" onClick={() => onMerge(c)} title="Объединить с другой категорией" aria-label="Объединить" className="rounded-lg p-1.5 text-text-muted hover:bg-bg-tertiary hover:text-text-primary">
          <ArrowsPointingInIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  </li>
);

const EditDialog = ({ category, rubrics, onClose, onSaved }) => {
  const [form, setForm] = useState({ name: category.name || "", kz_name: category.kz_name || "", eng_name: category.eng_name || "", ch_name: category.ch_name || "", image_url: category.image_url || "" });
  const [rubricId, setRubricId] = useState(category.rubric_id ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setError("Название (RU) обязательно.");
    if (!form.image_url.trim()) return setError("Нужна ссылка на изображение.");
    setSaving(true);
    try {
      const body = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v.trim() || (k === "name" || k === "image_url" ? v : null)]));
      const newRubric = rubricId === "" ? null : Number(rubricId);
      if (newRubric !== (category.rubric_id ?? null)) body.rubric_id = newRubric;
      await updateAdminCategory(category.id, body);
      onSaved(`«${form.name}» сохранена`);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && !saving && onClose()}>
      <form onSubmit={save} role="dialog" aria-modal="true" className="w-full max-w-lg rounded-block border border-border-primary bg-bg-card text-left shadow-lg">
        <div className="flex items-center justify-between border-b border-border-primary px-5 py-3.5">
          <h3 className="text-base font-semibold text-text-primary">Категория <span className="font-mono text-text-muted">#{category.id}</span></h3>
          <button type="button" onClick={onClose} aria-label="Закрыть" className="rounded-lg p-1 text-text-muted hover:bg-bg-tertiary"><XMarkIcon className="h-5 w-5" /></button>
        </div>
        <div className="space-y-3 p-5">
          {[["name", "Название (RU) *"], ["kz_name", "Атауы (KZ)"], ["eng_name", "Name (EN)"], ["ch_name", "名称 (CN)"]].map(([k, l], i) => (
            <label key={k} className="block">
              <span className="mb-1 block text-xs font-medium text-text-secondary">{l}</span>
              <input autoFocus={i === 0} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className={inputCls} />
            </label>
          ))}
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-text-secondary">Изображение (URL) *</span>
            <div className="flex gap-2">
              <input value={form.image_url} onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))} className={inputCls} />
              {form.image_url && <img src={form.image_url} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover" />}
            </div>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-text-secondary">Рубрика на главной сайта</span>
            <select value={rubricId} onChange={(e) => setRubricId(e.target.value)} className={inputCls}>
              <option value="">— Без рубрики (не видна в группах) —</option>
              {rubrics.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name_ru} #{r.id}
                </option>
              ))}
            </select>
          </label>
          {error && <p className="text-sm text-rose-500">{error}</p>}
        </div>
        <div className="flex justify-end gap-2 border-t border-border-primary px-5 py-3">
          <button type="button" onClick={onClose} className="rounded-lg border border-border-primary px-4 py-2 text-sm text-text-secondary hover:bg-bg-tertiary">Отмена</button>
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-accent-grad px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">
            {saving && <ArrowPathIcon className="h-4 w-4 animate-spin" />}Сохранить
          </button>
        </div>
      </form>
    </div>
  );
};

const MergeDialog = ({ source, categories, onClose, onDone }) => {
  const candidates = categories.filter((c) => c.id !== source.id && !c.is_hidden);
  const preferred = source.duplicates.map((id) => candidates.find((c) => c.id === id)).filter(Boolean)[0];
  const [targetId, setTargetId] = useState(preferred?.id || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const target = candidates.find((c) => c.id === Number(targetId));
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await mergeCategory(source.id, Number(targetId));
      onDone(
        `#${source.id} → #${targetId}: объявлений ${fmt(r.announcements_moved)}, подкатегорий объединено ${r.subcategories_merged}, перенесено ${r.subcategories_moved}` +
          (r.characteristics_kept_on_source ? ". Характеристики оставлены у исходной — проверьте форму объявления." : "")
      );
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div role="dialog" aria-modal="true" className="w-full max-w-lg rounded-block border border-border-primary bg-bg-card p-5 text-left shadow-lg">
        <h3 className="text-base font-semibold text-text-primary">Объединить «{source.name}» <span className="font-mono text-text-muted">#{source.id}</span></h3>
        <label className="mt-4 block">
          <span className="mb-1 block text-xs font-medium text-text-secondary">В какую категорию перенести</span>
          <select value={targetId} onChange={(e) => setTargetId(e.target.value)} className={inputCls}>
            <option value="">— Выберите —</option>
            {candidates.map((c) => (
              <option key={c.id} value={c.id}>
                {source.duplicates.includes(c.id) ? "★ " : ""}
                {c.name} #{c.id} · {c.stats.active_announcements} акт.
              </option>
            ))}
          </select>
        </label>
        {target && (
          <ul className="mt-4 list-disc space-y-1 pl-5 text-sm text-text-secondary">
            <li>все объявления, заявки и история поиска #{source.id} перейдут в «{target.name}»;</li>
            <li>одноимённые подкатегории объединятся, остальные ({source.subcategories_count} всего) перейдут в «{target.name}»;</li>
            <li>«{source.name}» #{source.id} будет скрыта (не удалена — можно вернуть).</li>
          </ul>
        )}
        {error && <p className="mt-3 text-sm text-rose-500">{error}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy} className="rounded-lg border border-border-primary px-4 py-2 text-sm text-text-secondary hover:bg-bg-tertiary">Отмена</button>
          <button type="button" onClick={run} disabled={busy || !target} className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-40">
            {busy && <ArrowPathIcon className="h-4 w-4 animate-spin" />}Объединить
          </button>
        </div>
      </div>
    </div>
  );
};

function CategoriesManagement() {
  const [items, setItems] = useState([]);
  const [state, setState] = useState("loading");
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [showHidden, setShowHidden] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);
  const [editing, setEditing] = useState(null);
  const [merging, setMerging] = useState(null);
  const [subsOf, setSubsOf] = useState(null);
  const [tab, setTab] = useState("categories");
  const [rubrics, setRubrics] = useState([]);

  useEffect(() => {
    if (!toast || toast.type === "error") return undefined;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const load = useCallback(async (refresh = false) => {
    setState((s) => (s === "ready" ? "ready" : "loading"));
    try {
      const [cats, rub] = await Promise.all([listAdminCategories(refresh), listAdminRubrics().catch(() => null)]);
      setItems(cats);
      if (rub) setRubrics(rub.items || []);
      setState("ready");
    } catch (err) {
      setError(err.message);
      setState("error");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const byId = useMemo(() => Object.fromEntries(items.map((c) => [c.id, c])), [items]);
  const rubricName = useMemo(() => Object.fromEntries(rubrics.map((r) => [r.id, r.name_ru])), [rubrics]);
  const noRubric = items.filter((c) => !c.is_hidden && !c.rubric_id).length;
  const pinned = items.filter((c) => c.is_pinned && !c.is_hidden).sort((a, b) => a.sort_order - b.sort_order);
  const maxScore = Math.max(...items.map((c) => c.score), 1);
  const q = query.trim().toLowerCase();
  const filtered = items.filter((c) => (showHidden || !c.is_hidden) && (!q || `${c.name} ${c.kz_name || ""} #${c.id}`.toLowerCase().includes(q)));
  const dupPairs = useMemo(() => {
    const seen = new Set();
    return items
      .filter((c) => !c.is_hidden && c.duplicates.some((d) => byId[d] && !byId[d].is_hidden))
      .filter((c) => {
        const key = [c.id, ...c.duplicates].sort().join("-");
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [items, byId]);

  const act = async (c, fn, message) => {
    setBusyId(c.id);
    try {
      await fn();
      setToast({ type: "success", text: message });
      await load();
    } catch (err) {
      setToast({ type: "error", text: err.message });
    } finally {
      setBusyId(null);
    }
  };

  const togglePin = (c) => {
    const ids = c.is_pinned ? pinned.filter((p) => p.id !== c.id).map((p) => p.id) : [...pinned.map((p) => p.id), c.id];
    act(c, () => setPinnedCategories(ids), c.is_pinned ? `«${c.name}» откреплена` : `«${c.name}» закреплена (${ids.length}-я)`);
  };

  const move = (c, dir) => {
    const ids = pinned.map((p) => p.id);
    const i = ids.indexOf(c.id);
    [ids[i], ids[i + dir]] = [ids[i + dir], ids[i]];
    act(c, () => setPinnedCategories(ids), "Порядок сохранён");
  };

  const toggleHidden = (c) =>
    act(c, () => updateAdminCategory(c.id, { is_hidden: !c.is_hidden }), c.is_hidden ? `«${c.name}» снова видна` : `«${c.name}» скрыта из приложения и сайта`);

  return (
    <div className="space-y-4 text-left">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-text-primary">Каталог</h2>
          <p className="mt-0.5 text-sm text-text-secondary">Рубрики, категории и подкатегории — порядок в приложении и на сайте</p>
        </div>
        <button type="button" hidden={tab !== "categories"} onClick={() => load(true)} className="inline-flex items-center gap-2 rounded-lg border border-border-primary px-3 py-2 text-sm font-medium text-text-secondary hover:bg-bg-tertiary" title="Пересчитать популярность сейчас (обычно раз в час)">
          <ArrowPathIcon className={`h-4 w-4 ${state === "loading" ? "animate-spin" : ""}`} /> Пересчитать
        </button>
      </div>

      <div role="tablist" className="flex gap-1 border-b border-border-primary">
        {[
          ["categories", `Категории (${items.filter((c) => !c.is_hidden).length})`],
          ["rubrics", `Рубрики (${rubrics.length})${noRubric ? ` · без рубрики ${noRubric}` : ""}`],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${tab === id ? "border-[#4F45E5] text-text-primary" : "border-transparent text-text-secondary hover:text-text-primary"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "rubrics" ? (
        <RubricsPanel onCategoriesChanged={() => load()} />
      ) : (
      <>
      <p className="flex items-start gap-2 rounded-lg border border-border-primary bg-bg-card px-4 py-2.5 text-xs text-text-secondary">
        <InformationCircleIcon className="h-4 w-4 shrink-0 text-text-muted" />
        Популярность = 5 × поисков за 30 дней + 3 × активных объявлений + √просмотров. Пересчитывается раз в час. Закреплённые всегда наверху в заданном порядке.
      </p>

      {toast && (
        <div role={toast.type === "error" ? "alert" : "status"} className={`flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${toast.type === "error" ? "border-rose-500/30 bg-rose-500/10 text-rose-500" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-500"}`}>
          <span>{toast.text}</span>
          <button type="button" onClick={() => setToast(null)} aria-label="Закрыть" className="shrink-0 opacity-70 hover:opacity-100"><XMarkIcon className="h-4 w-4" /></button>
        </div>
      )}

      {dupPairs.length > 0 && (
        <section className="rounded-block border border-amber-500/30 bg-amber-500/[0.07] p-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-amber-500">
            <ExclamationTriangleIcon className="h-5 w-5" /> Найдено дублей: {dupPairs.length}
          </h3>
          <p className="mt-0.5 text-xs text-text-secondary">Пользователи видят одну и ту же категорию дважды, объявления разделены. Объедините меньшую в большую.</p>
          <ul className="mt-3 flex flex-wrap gap-2">
            {dupPairs.map((c) => {
              const group = [c, ...c.duplicates.map((d) => byId[d]).filter((d) => d && !d.is_hidden)].sort((a, b) => b.stats.active_announcements - a.stats.active_announcements);
              const [main, ...rest] = group;
              return (
                <li key={c.id} className="flex items-center gap-2 rounded-lg border border-border-primary bg-bg-card px-3 py-1.5 text-xs">
                  <span className="text-text-primary">{main.name}</span>
                  <span className="text-text-muted">
                    #{main.id} ({main.stats.active_announcements}) ← {rest.map((r) => `#${r.id} (${r.stats.active_announcements})`).join(", ")}
                  </span>
                  <button type="button" onClick={() => setMerging(rest[0])} className="rounded-md bg-amber-500 px-2 py-0.5 font-semibold text-white hover:bg-amber-600">
                    Объединить
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {state === "error" ? (
        <div className="rounded-block border border-border-primary bg-bg-card px-6 py-14 text-center">
          <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-rose-500/80" />
          <p className="mt-2 text-sm text-text-secondary">Не удалось загрузить категории</p>
          <p className="text-xs text-text-muted">{error}</p>
          <button type="button" onClick={() => load()} className="mt-3 rounded-lg border border-border-primary px-3 py-1.5 text-sm text-text-secondary hover:bg-bg-tertiary">Повторить</button>
        </div>
      ) : (
        <section className="overflow-hidden rounded-block border border-border-primary bg-bg-card shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border-primary px-4 py-3">
            <div className="relative w-full sm:w-72">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Название или #ID" aria-label="Поиск категории" className={`${inputCls} pl-9`} />
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-text-secondary">
              <input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} className="h-4 w-4 accent-[#4F45E5]" />
              Показывать скрытые ({items.filter((c) => c.is_hidden).length})
            </label>
          </div>
          {state === "loading" && items.length === 0 ? (
            <ul className="divide-y divide-border-primary">
              {Array.from({ length: 8 }).map((_, i) => (
                <li key={i} className="flex animate-pulse items-center gap-3 px-4 py-3">
                  <div className="h-10 w-10 rounded-lg bg-bg-tertiary" />
                  <div className="h-3 flex-1 rounded bg-bg-tertiary" />
                </li>
              ))}
            </ul>
          ) : (
            <ul className="divide-y divide-border-primary">
              {filtered.map((c) => (
                <Row
                  key={c.id}
                  c={c}
                  byId={byId}
                  maxScore={maxScore}
                  busy={busyId === c.id}
                  first={pinned[0]?.id === c.id}
                  last={pinned[pinned.length - 1]?.id === c.id}
                  onPin={togglePin}
                  onMove={move}
                  onToggleHidden={toggleHidden}
                  onEdit={setEditing}
                  onMerge={setMerging}
                  onSubs={setSubsOf}
                  rubricName={rubricName}
                />
              ))}
              {filtered.length === 0 && <li className="px-4 py-10 text-center text-sm text-text-muted">Ничего не найдено</li>}
            </ul>
          )}
        </section>
      )}
      </>
      )}

      {editing && (
        <EditDialog
          category={editing}
          rubrics={rubrics}
          onClose={() => setEditing(null)}
          onSaved={(m) => {
            setEditing(null);
            setToast({ type: "success", text: m });
            load();
          }}
        />
      )}
      {subsOf && (
        <SubcategoriesDialog
          category={subsOf}
          categories={items}
          onClose={(changed) => {
            setSubsOf(null);
            if (changed) load();
          }}
        />
      )}
      {merging && (
        <MergeDialog
          source={merging}
          categories={items}
          onClose={() => setMerging(null)}
          onDone={(m) => {
            setMerging(null);
            setToast({ type: "success", text: m });
            load(true);
          }}
        />
      )}
    </div>
  );
}

export default CategoriesManagement;
